import { fetchAvsWorkspaceId } from '@jira/router-resources__itam/src/avsWorkspaceResource.tsx';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { HamViewsResponse } from './hamViewsTypes.tsx';

export const HAM_VIEWS_RESOURCE_KEY = 'ITAM_HAM_DASHBOARD_VIEWS';

type ErrorWithStatusCode = {
	statusCode?: number;
	extensions?: { statusCode?: number };
};

const getStatusCodeFromError = (error: unknown): number | undefined => {
	const candidate: ErrorWithStatusCode = typeof error === 'object' && error != null ? error : {};
	return candidate.statusCode ?? candidate.extensions?.statusCode;
};

export class ViewsApiError extends Error {
	readonly isViewsApiError = true;

	readonly statusCode: number | undefined;

	constructor(cause: unknown) {
		super(cause instanceof Error ? cause.message : 'Failed to fetch HAM views');
		this.name = 'ViewsApiError';
		// Preserve the underlying HTTP status (e.g. 403/401 from FetchError) so consumers can
		// distinguish a forbidden response and render the appropriate UI.
		this.statusCode = getStatusCodeFromError(cause);
	}
}

export const hamViewsResource: RouteResource<HamViewsResponse> = createResource<HamViewsResponse>({
	type: HAM_VIEWS_RESOURCE_KEY,
	getKey: ({ match }) => {
		const practice = match.params.practice ?? '';
		return `${HAM_VIEWS_RESOURCE_KEY}_${practice}`;
	},
	getData: async (_routerContext, { tenantContext }) => {
		const cloudId = tenantContext?.cloudId;
		if (!cloudId) {
			throw new Error('Unable to fetch HAM views: missing tenantContext.cloudId');
		}
		const avsWorkspaceId = await fetchAvsWorkspaceId(cloudId);
		if (avsWorkspaceId == null) {
			throw new Error('AVS workspace ID not available');
		}
		try {
			return await performGetRequest<HamViewsResponse>(
				`/gateway/api/jsm/reporting/assets-vertical/workspace/${avsWorkspaceId}/vertical/ham/v1/views`,
			);
		} catch (error) {
			// Wrap failures originating from the views API specifically, so consumers can distinguish
			// them from earlier failures in getData (e.g. missing cloudId or the AVS workspace-id
			// resolution). Only a views-API failure should suppress the recovery refresh.
			throw new ViewsApiError(error);
		}
	},
	maxAge: 1000 * 60 * 5, // 5 minutes
});
